import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { installBackButton } from './back-button';
import { initFullVersion } from './full-version';
import { App } from './ui/App';
import '@fontsource-variable/inter';
import '@fontsource/barlow/500.css';
import '@fontsource/barlow/700.css';
import '@fontsource/barlow-condensed/600.css';
import '@fontsource/barlow-condensed/700.css';
import './styles.css';
import './theme.css';
import './academy.css';
import './settings.css';
import './mobile.css';
import { checkSession, onAccountChange } from './account';
import { diag } from './diagnostics';
import { fullscreenOnFirstTouch } from './fullscreen';
import { loadLanguage } from './i18n';
import { readKeyboardLayout } from './keyboard';
import './splash';
import { sound } from './sound';
import { useGame } from './store';

const root = document.getElementById('root');
if (!root) throw new Error('#root missing');

// Safari zooms the page with a pinch even where zooming is turned off: the game's own pinch only.
document.addEventListener('gesturestart', (e) => e.preventDefault());
// A phone's browser: full screen at the first touch (fullscreen.ts).
fullscreenOnFirstTouch();

// Crash, error and stall reports from the start (the player may have turned them off).
diag.enabled = useGame.getState().controls.reports;
diag.start();
// The Android app's back button (see back-button.ts).
installBackButton();
// The free Android game: what Google Play says the player owns (see full-version.ts).
void initFullVersion();

// Development: `__makeThumbs()` renders the catalog's building pictures with the game open and
// saves them into public/art/thumbs (commit them; see render/thumbnails.ts).
if (import.meta.env.DEV) {
  (window as unknown as { __makeThumbs: () => Promise<number> }).__makeThumbs = async () => {
    const game = (
      window as unknown as {
        __game?: { renderer: { thumbnails(): Promise<Record<number, string>> } };
      }
    ).__game;
    if (!game) throw new Error('open a game first');
    const { BUILDINGS } = await import('@am/sim');
    const pictures = await game.renderer.thumbnails();
    let n = 0;
    for (const [type, url] of Object.entries(pictures)) {
      const key = BUILDINGS[Number(type) as keyof typeof BUILDINGS]?.key;
      if (!key) continue;
      const blob = await (await fetch(url)).blob();
      await fetch(`/__thumbs?name=${key}.png`, { method: 'POST', body: blob });
      n++;
    }
    return n;
  };
}

// For looking into a report: `__openWorld()` in the console opens a report's .amsave (or any
// save file) as an offline game (see docs/RAPORLAR.md).
(window as unknown as { __openWorld: () => void }).__openWorld = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    const data = new Uint8Array(await file.arrayBuffer());
    useGame.setState({
      screen: 'game',
      startMode: { kind: 'offline', save: { id: 'report', name: file.name, data } },
    });
  };
  input.click();
};

// The saved sound volume, before any sound plays.
sound.setVolume(useGame.getState().volume);
sound.setMix(useGame.getState().soundMix);

// Every button, tab and catalogue card answers with a soft click, as a game's menus do.
document.addEventListener(
  'pointerdown',
  (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const control = target?.closest('button, .build-card, [role="tab"]');
    if (control && !(control instanceof HTMLButtonElement && control.disabled)) sound.play('click');
  },
  { capture: true },
);

// Signing in or out (or a session found expired) shows at once on the title screen.
onAccountChange((account) => useGame.setState({ account }));
void checkSession();

// The page speaks the game's language: capitals (text-transform) follow its rules, so an "i"
// becomes "I" in German and "İ" only in Turkish.
document.documentElement.lang = useGame.getState().lang;
useGame.subscribe((s, prev) => {
  if (s.lang !== prev.lang) document.documentElement.lang = s.lang;
});

// Russian, French and German come in a file of their own: in before the first word is shown
// (English stands in if it cannot be fetched).
// The keyboard's layout too (key names as printed on it: keyboard.ts).
void Promise.all([loadLanguage(useGame.getState().lang), readKeyboardLayout()]).finally(() =>
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  ),
);
